import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';

import { configuredDrugKeys } from '../../../constants';
import { useDrugProgram } from '../../../contexts/DrugProgramProvider';
import { getFragment } from '../../../graphql/client/helpers/get-fragment';
import {
    type DigitallyReceivedPrescriberOrderInfoFragment,
    SendPrescriberOrderDocument,
} from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { usePrescriberOrder } from '../../../hooks/use-prescriber-order';
import { useRefetchQueries } from '../../../hooks/use-refetch-queries';
import { getDrugConfigByMedication } from '../../../util/config/get-drug-config-by-medication';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { getRefetchQueryForPrescription } from '../../../util/get-refetch-query-for-prescription';
import { getRedirectUrlByOrderStatus } from '../../../util/orders';

import PreviewDetails from './PreviewDetails';

const OrderDetailsPage = () => {
    const { medication, order, orderId } = usePrescriberOrder();
    const { patient: userPatient } = usePatientContext();
    const navigate = useNavigate();
    const drugProgram = useDrugProgram();

    const [sendPrescriberOrder, { loading: isSendLoading }] = useMutation(
        SendPrescriberOrderDocument
    );

    const {
        refetch,
        isComplete: isRefetchComplete,
        isLoading: isRefetchLoading,
    } = useRefetchQueries(
        getRefetchQueryForPrescription(orderId, userPatient.id)
    );

    useEffect(() => {
        if (isRefetchComplete) {
            navigate(
                getAbsoluteRoute('order.prescriberOrderId.confirmation', {
                    prescriberOrderId: orderId,
                })
            );
        }
    }, [isRefetchComplete, navigate, orderId]);

    const handleSend = useCallback(
        async (providerId: string) => {
            const result = await sendPrescriberOrder({
                variables: { prescriberOrderId: orderId, providerId },
            });
            if (result.data?.sendPrescriberOrder?.success) {
                void refetch();
            } else {
                throw new Error('Failed to send prescriber order');
            }
        },
        [orderId, sendPrescriberOrder, refetch]
    );

    const autoRedirectUrl = getRedirectUrlByOrderStatus(order);

    if (autoRedirectUrl) {
        return <Navigate to={autoRedirectUrl} />;
    }

    // If there's no automatic redirect, we know this is a valid order
    const prescriberOrder =
        order as DigitallyReceivedPrescriberOrderInfoFragment;

    const drugConfig = getDrugConfigByMedication(drugProgram, medication);

    if (
        !drugConfig?.drugKey ||
        !configuredDrugKeys.includes(drugConfig.drugKey)
    ) {
        return (
            <Navigate
                to={getAbsoluteRoute('order.prescriberOrderId.root', {
                    prescriberOrderId: prescriberOrder?.drxId ?? '',
                })}
            />
        );
    }
    const patient = getFragment(prescriberOrder.patient);

    return (
        <PreviewDetails
            prescriberOrder={prescriberOrder}
            patient={patient}
            sendPrescriberOrder={handleSend}
            isSendLoading={isSendLoading || isRefetchLoading}
        />
    );
};

export default OrderDetailsPage;
